// Shared list-table kit — the canonical look & behavior for every list
// page in the manager, extracted from the Agents list. Pages compose:
//
//   <TableCard minWidth={1060} footer={<TablePagination …/>}>
//     <TableHeaderRow>
//       <HeaderFilter …/> | <HeaderLabel …/> | <SortHeader …/>
//     </TableHeaderRow>
//     {error ? <TableMessage tone="destructive">…</TableMessage>
//       : items === null ? <TableMessage>Loading…</TableMessage>
//       : items.length === 0 ? <EmptyState …/>
//       : items.map(…)}
//   </TableCard>
//
// Rows stay page-owned (each list has its own cells) — the kit owns the
// shell, header chrome, sort/filter affordances, status messages, and
// pagination so they can't drift apart again.
import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronDown,
  Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import { Card } from '../ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';

// Card shell with horizontal scroll. `minWidth` keeps columns readable
// on narrow viewports; `footer` (usually <TablePagination>) renders
// outside the scroll area so it never scrolls off-screen.
export function TableCard({
  minWidth,
  footer,
  busy = false,
  children,
}: {
  minWidth: number;
  footer?: React.ReactNode;
  /** A refetch is in flight while rows are already on screen (filter,
   *  sort or page change). Dims the current rows and shows a small
   *  "Updating" pill instead of blanking the table. */
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    // Fills the space left under the page header (AppShell's content
    // column is a flex column), so a short list doesn't leave dead space
    // beneath it; with enough rows the rows scroll inside and the page
    // itself stays put.
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
      <div className="relative flex flex-1 flex-col overflow-x-auto">
        <div
          className={cn(
            'flex flex-1 flex-col transition-opacity duration-150',
            busy && 'pointer-events-none opacity-40',
          )}
          style={{ minWidth }}
        >
          {children}
        </div>
        {busy && (
          // Fixed, not absolute: a long list scrolls with the page, so
          // anchoring the pill to the card would park it off-screen.
          // Viewport-centred keeps it in view at any scroll position.
          <div
            className="pointer-events-none fixed inset-0 z-30 flex items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 shadow-lg">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              <span className="mono-label text-muted-foreground">
                Updating
              </span>
            </span>
          </div>
        )}
      </div>
      {footer}
    </Card>
  );
}

export function TableHeaderRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-3">
      {children}
    </div>
  );
}

export function HeaderLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mono-label text-muted-foreground', className)}>
      {children}
    </div>
  );
}

export function SortHeader<K extends string>({
  label,
  sortKey,
  sortBy,
  order,
  onSort,
  className,
}: {
  label: string;
  sortKey: K;
  sortBy: K;
  order: 'asc' | 'desc';
  onSort: (key: K) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'mono-label flex items-center justify-start gap-1 text-muted-foreground transition-colors hover:text-foreground',
        active && 'text-foreground',
        className,
      )}
    >
      <span>{label}</span>
      {active &&
        (order === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        ))}
    </button>
  );
}

// Filter dropdown living in the header row (e.g. the Agents status
// filter, the Inquiries qualification filter). Resets to page 1 is the
// caller's job inside onChange.
export function HeaderFilter<V extends string>({
  value,
  options,
  onChange,
  className,
  menuClassName = 'min-w-[14rem]',
}: {
  value: V;
  /** `count` renders as a tally pill after the label, so the dropdown
   *  shows how many rows each filter would return. */
  options: ReadonlyArray<{ value: V; label: string; count?: number }>;
  onChange: (value: V) => void;
  className?: string;
  menuClassName?: string;
}) {
  const current = options.find((o) => o.value === value)?.label ?? value;
  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="mono-label group inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
          >
            <span>{current}</span>
            <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className={menuClassName}>
          {options.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onChange(opt.value)}
              className={cn(
                'flex items-center justify-between gap-4',
                opt.value === value && 'text-foreground',
              )}
            >
              <span className="min-w-0 truncate">{opt.label}</span>
              {opt.count !== undefined && (
                <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                  {opt.count.toLocaleString()}
                </span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Centered status line for loading / error states inside the body.
export function TableMessage({
  tone = 'muted',
  children,
}: {
  tone?: 'muted' | 'destructive';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-1 items-center justify-center px-5 py-12 text-center font-mono text-[11px]',
        tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}

// Loading placeholder for a list body — a spinner rather than bare
// "Loading…" text, centred in the card the same way the empty state is.
export function TableLoading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-5 py-12">
      <Loader2
        className="h-5 w-5 animate-spin text-muted-foreground"
        aria-hidden
      />
      <span className="mono-label text-muted-foreground">{label}</span>
      <span className="sr-only" role="status">
        {label}
      </span>
    </div>
  );
}

const DEFAULT_PAGE_SIZES = [10, 25, 50];

export function TablePagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
}: {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-3.5">
      <div className="mono-label text-muted-foreground">
        Showing{' '}
        <span className="text-foreground">
          {start}–{end}
        </span>{' '}
        of <span className="text-foreground">{total}</span> results
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="mono-label text-muted-foreground">Page limit</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                {pageSize}
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[5rem]">
              {pageSizeOptions.map((size) => (
                <DropdownMenuItem
                  key={size}
                  onSelect={() => onPageSizeChange(size)}
                  className={cn(size === pageSize && 'text-foreground')}
                >
                  {size}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
